/**
 * FAQ content page at `/faq` (#241, FR-15.5, D241.6/D241.7) — a standalone, crawlable page
 * targeting question-intent keywords ("AI trivia generator", "custom quiz buzzer game",
 * "virtual team trivia"; see `docs/SEO_KEYWORDS.md`). The visible Q&A and the `FAQPage`
 * JSON-LD both derive from {@link FAQ_ITEMS} (one source — Google requires the structured
 * data to match the on-page text).
 *
 * SSR-safe (no `window`/timers at render top-level) so the prerender bakes it into a
 * crawlable `dist/faq/index.html` with the `FAQPage` structured data in `<head>`. The head
 * (incl. the JSON-LD) comes from {@link headModelFor} via {@link useDocumentHead}. The
 * site-wide {@link ../ui/Footer} is rendered by the App layout below every route.
 */

import { Link } from 'react-router-dom';
import { BRAND_NAME } from '../ui/brand';
import { LandingHeader } from '../ui/landing/LandingHeader';
import { useDocumentHead } from '../seo/useDocumentHead';
import { FAQ_ITEMS, headModelFor } from '../seo/route-seo';

export function FaqPage(): React.JSX.Element {
  useDocumentHead(headModelFor('/faq'));

  return (
    <div className="landing" data-testid="faq-page">
      <LandingHeader />

      <header className="landing-wrap landing-hero">
        <div className="landing-hero__copy">
          <span className="landing-eyebrow">FAQ</span>
          <h1 className="landing-hero__title">
            {BRAND_NAME} <span className="landing-amber">questions</span>, answered
          </h1>
          <p className="landing-hero__lead">
            Everything you need to know about the AI trivia generator, live buzzer party
            play, and getting a game going.
          </p>
        </div>
      </header>

      <section className="landing-wrap landing-section" data-testid="faq-list">
        <dl className="faq-list">
          {FAQ_ITEMS.map((item) => (
            <div className="faq-item" key={item.q} data-testid="faq-item">
              <dt className="faq-q">{item.q}</dt>
              <dd className="faq-a">{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="landing-wrap landing-section landing-why" data-testid="faq-cta">
        <div className="landing-sec-head">
          <h2>Ready to play?</h2>
          <p>Pick your topics and spin up a board — free to try, no download.</p>
        </div>
        <Link
          className="btn btn--primary landing-cta"
          to="/host"
          data-testid="faq-cta-host"
        >
          Host a game →
        </Link>
      </section>
    </div>
  );
}
